import { useState, useEffect } from "react";
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { SleepRecord } from "@/types";
import { calculateLatency, calculateAverageLatency } from "@/utils/sleep-analysis";

export interface CaregiverProfile {
  caregiver_id: string;
  auth_user_id: string;
  caregiver_name: string;
  caregiver_email: string;
  last_activity: string;
  status: string;
  active: boolean;
  days_left: number | null;
  agreement_expired: boolean;
  roomId: string; 
  total_count: number;
  specialist_id?: string | null;
  timezone?: string;
  timezoneDisplay?: string;
}

export interface Child {
  id: string;
  name: string;
  created_at: string;
}

export interface ChildSleepData {
  childId: string;
  latest: number | 'N/A';
  average: number | 'N/A';
  records: SleepRecord[];
}

export function useCaregiverProfileData(caregiverId: string | undefined) {
  const [refreshKey, setRefreshKey] = useState(0);
  const [selectedChild, setSelectedChild] = useState<Child | null>(null);

  // Fetch caregiver data
  const { 
    data: caregiver, 
    isLoading: isLoadingCaregiver 
  } = useQuery<CaregiverProfile, Error>({
    queryKey: ['caregiver', caregiverId, refreshKey] as const,
    queryFn: async () => {
      if (!caregiverId) throw new Error('No caregiver ID provided');

      // Define the type for caregiverData to include specialist_id and timezone
      interface CaregiverData {
        id: string;
        auth_user_id: string;
        name: string;
        created_at: string;
        updated_at: string;
        last_activity: string;
        last_activity_type: string;
        active: boolean;
        specialist_id?: string | null;
        timezone?: string;
      }

      const { data: caregiverData, error: caregiverError } = await supabase
        .from('caregivers')
        .select('*')
        .eq('id', caregiverId)
        .single();

      if (caregiverError) throw caregiverError;
      if (!caregiverData) throw new Error('Caregiver not found');

      // Type assertion to ensure TypeScript recognizes the caregiverData structure
      const typedCaregiverData = caregiverData as CaregiverData;

      // Fetch latest agreement
      const { data: agreements, error: agreementError } = await supabase
        .from('agreements')
        .select('*')
        .eq('caregiver_id', caregiverId)
        .order('created_at', { ascending: false })
        .limit(1);

      if (agreementError) throw agreementError;

      const latestAgreement = agreements?.[0];
      const daysLeft = latestAgreement 
        ? Math.round((new Date(latestAgreement.end_date).getTime() - new Date().getTime()) / (1000 * 60 * 60 * 24))
        : null;

      // Use the local browser timezone if not available in caregiver data
      const timezone = typedCaregiverData.timezone || Intl.DateTimeFormat().resolvedOptions().timeZone;
      
      // Get a human-readable timezone name for display purposes
      let timezoneDisplay = timezone;
      try {
        // Try to get a more readable timezone abbreviation (like PST, EST, etc.)
        const timezoneParts = new Intl.DateTimeFormat('en-US', { 
          timeZoneName: 'short', 
          timeZone: timezone 
        }).formatToParts(new Date());
        
        const tzPart = timezoneParts.find(part => part.type === 'timeZoneName');
        if (tzPart && tzPart.value) {
          timezoneDisplay = tzPart.value;
        }
      } catch (error) {
        console.warn('Unable to format timezone:', error);
        // Fall back to the raw timezone value
      }

      return {
        caregiver_id: typedCaregiverData.id,
        auth_user_id: typedCaregiverData.auth_user_id,
        caregiver_name: typedCaregiverData.name,
        caregiver_email: typedCaregiverData.auth_user_id,
        last_activity: typedCaregiverData.last_activity,
        status: typedCaregiverData.last_activity_type || 'unknown',
        active: typedCaregiverData.active,
        days_left: daysLeft,
        agreement_expired: daysLeft !== null && daysLeft <= 0,
        roomId: '',
        total_count: 0,
        specialist_id: typedCaregiverData.specialist_id,
        timezone: timezone,
        timezoneDisplay: timezoneDisplay
      };
    },
    retry: 1,
    refetchOnWindowFocus: false
  });

  // Fetch children for this caregiver
  const { 
    data: children, 
    isLoading: isLoadingChildren 
  } = useQuery<Child[], Error>({
    queryKey: ['caregiver-children', caregiverId] as const,
    queryFn: async () => {
      if (!caregiverId) throw new Error('No caregiver ID provided');

      const { data, error } = await supabase
        .from('children')
        .select('*')
        .eq('caregiver_id', caregiverId);

      if (error) throw error;
      return data || [];
    },
    enabled: !!caregiverId
  });

  // Set initial selected child
  useEffect(() => {
    if (children?.length > 0 && !selectedChild) {
      setSelectedChild(children[0]);
    }
  }, [children, selectedChild]);

  // Fetch sleep data for selected child
  const { 
    data: sleepData, 
    isLoading: isLoadingSleep 
  } = useQuery<ChildSleepData[], Error>({
    queryKey: ['sleep-data', caregiverId, selectedChild?.id] as const,
    queryFn: async () => {
      if (!caregiverId || !selectedChild) throw new Error('Missing required IDs');

      const { data: sleepRecords, error: sleepError } = await supabase
        .from('sleep_sessions')
        .select(`
          id,
          child_id,
          start_time,
          end_time,
          environment:environments(description),
          onset_method:onset_methods(method),
          restfulness:restfulness(rating)
        `)
        .eq('child_id', selectedChild.id)
        .order('start_time', { ascending: false });

      if (sleepError) throw sleepError;

      const records = sleepRecords?.map(record => {
        // Calculate duration in minutes for each record
        const startTime = new Date(record.start_time);
        const endTime = new Date(record.end_time);
        const durationMinutes = Math.round((endTime.getTime() - startTime.getTime()) / (1000 * 60));
        
        return {
          id: record.id,
          child_id: record.child_id,
          start_time: record.start_time,
          end_time: record.end_time,
          duration_minutes: durationMinutes,
          environment: record.environment,
          onset_method: record.onset_method,
          restfulness: record.restfulness
        };
      }) || [];

      return [{
        childId: selectedChild.id,
        records,
        latest: calculateLatency(records[0]),
        average: calculateAverageLatency(records)
      }];
    },
    enabled: !!caregiverId && !!selectedChild
  });

  const handleChildChange = (childId: string) => {
    const newSelectedChild = children?.find(child => child.id === childId);
    if (newSelectedChild) {
      setSelectedChild(newSelectedChild);
    }
  };

  const handleAgreementUpdate = () => {
    setRefreshKey(prev => prev + 1);
  };

  const isLoading = isLoadingCaregiver || isLoadingChildren || isLoadingSleep;
  const selectedChildSleepData = sleepData?.[0];

  return {
    caregiver,
    children,
    selectedChild,
    sleepData,
    selectedChildSleepData,
    isLoading,
    handleChildChange,
    handleAgreementUpdate
  };
}
